import type {
    PrintModalContext,
    PrintProductItem,
    PrintRequestPayload,
} from '@/types/utils/print.utils.types';
import { axiosEc } from '@/config/axios.config';
import { useAppPrintStore } from '@/stores/print.store';
import { tourTooltipService } from '@/utils/tour-tooltip.utils';

/**
 * 列印工具
 */
const forceCloseTour = (): void => {
    tourTooltipService.closeAll({
        emitClose: true,
    });
    tourTooltipService.clearControllerContext();
};

export const PrintUtils = {
    /**
     * 取得列印模式狀態
     */
    GetPrintStatus: (): boolean => {
        const printStore = useAppPrintStore();
        return printStore.isPrinting;
    },

    /**
     * 設定列印模式狀態
     */
    SetPrintStatus: (value: boolean): void => {
        const printStore = useAppPrintStore();
        printStore.setPrintStatus(value);
        window.dispatchEvent(new Event('UpdatePrintStatus'));
    },

    /**
     * 取得列印項目
     */
    GetPrintItems: (): PrintProductItem[] => {
        const printStore = useAppPrintStore();
        return printStore.selectedPrintItems;
    },

    /**
     * 傳遞產品清單給其他頁面
     */
    SendProductSubMains: (productSubMains: PrintProductItem[]): void => {
        window.dispatchEvent(
            new CustomEvent('UpdateProductSubMains', {
                detail: {
                    productSubMains,
                },
            })
        );
    },

    /**
     * 切換列印模式
     */
    TogglePrintMode: (): void => {
        const printStore = useAppPrintStore();
        printStore.togglePrintMode();

        if (printStore.isPrinting) {
            forceCloseTour();
        }

        window.dispatchEvent(new Event('UpdatePrintStatus'));
    },

    /**
     * 儲存勾選項目到伺服器
     */
    AddPrintItem: async (
        url: string,
        data: PrintRequestPayload
    ): Promise<void> => {
        try {
            const reqData = {
                RequestId: '',
                Data: data,
            };
            await axiosEc.post(url, reqData);
        } catch (error) {
            console.error('列印資料儲存失敗：', error);
        }
    },

    /**
     * 顯示錯誤訊息
     */
    ExportErrorAlert: (context: PrintModalContext, msg: string): void => {
        context.$refs.printMsgModal.confirm({
            title: '輸出提醒',
            body: msg,
            showReject: false,
            size: 'sm',
        });
    },

    /**
     * 檢查是否已勾選列印項目
     */
    IsPrintItemsExists: (productClass: PrintProductItem): boolean => {
        const printStore = useAppPrintStore();
        return printStore.isPrintItemsExists(productClass);
    },

    /**
     * 切換勾選列印項目
     */
    TogglePrintItem: (
        productClass: PrintProductItem,
        sortFunc: () => void
    ): void => {
        const printStore = useAppPrintStore();
        const hasAdded = printStore.togglePrintItem(productClass);
        if (hasAdded) {
            sortFunc();
        }

        window.dispatchEvent(new Event('UpdatePrintItems'));
        window.dispatchEvent(new Event('SelectActiveHandler'));
    },

    /**
     * 清空列印項目
     */
    RemoveAllPrintItem: (): void => {
        const printStore = useAppPrintStore();
        printStore.removeAllPrintItems();
        window.dispatchEvent(new Event('UpdatePrintItems'));
        window.dispatchEvent(new Event('SelectActiveHandler'));
    },

    /**
     * 勾選所有列印項目
     */
    AddAllPrintItem: (productSubMains: PrintProductItem[]): void => {
        const printStore = useAppPrintStore();
        printStore.addAllPrintItems(productSubMains);

        window.dispatchEvent(new Event('UpdatePrintItems'));
        window.dispatchEvent(new Event('SelectActiveHandler'));
    },
} as const;
